import type { CalendarDate } from "../shared/calendar-date";
import { calculateDailyCalorieFloor } from "./daily-floor-policy";
import { validateWeekPlan, type DailyCalorieAllocation, type WeekPlan } from "./week-plan";

export interface RedistributableCalorieAllocation {
  readonly date: CalendarDate;
  readonly calories: number;
  readonly minimumCalories: number;
  readonly isLocked: boolean;
}

/**
 * Changes one day while sharing the inverse change as evenly as whole calories
 * allow across every other unlocked day. Locked days are never changed and a
 * requested increase is rejected atomically when the unlocked donors cannot
 * fund it without crossing their individual floors.
 */
export function redistributeCaloriesEvenly<T extends RedistributableCalorieAllocation>(
  allocations: readonly T[],
  editedDate: CalendarDate,
  requestedCalories: number,
): T[] {
  if (!Number.isSafeInteger(requestedCalories)) {
    throw new Error("Daily calories must be a whole number");
  }

  const dates = new Set<CalendarDate>();
  for (const allocation of allocations) {
    if (dates.has(allocation.date)) throw new Error("Daily allocation dates must be unique");
    dates.add(allocation.date);
    if (!Number.isSafeInteger(allocation.calories) || !Number.isSafeInteger(allocation.minimumCalories)) {
      throw new Error("Daily allocations and floors must be whole numbers");
    }
    if (allocation.minimumCalories < 0 || allocation.calories < allocation.minimumCalories) {
      throw new Error(`Daily allocation ${allocation.date} is below its calorie floor`);
    }
    if (typeof allocation.isLocked !== "boolean") throw new Error("Daily allocation lock state must be a boolean");
  }

  const edited = allocations.find((allocation) => allocation.date === editedDate);
  if (!edited) throw new Error("Edited allocation date must belong to the week");
  if (edited.isLocked) throw new Error("Unlock this day before changing its calories");
  if (requestedCalories < edited.minimumCalories) {
    throw new Error(`Daily calories cannot be below the floor of ${edited.minimumCalories.toLocaleString()} kcal`);
  }

  const delta = requestedCalories - edited.calories;
  if (delta === 0) return allocations.map((allocation) => ({ ...allocation }));

  const peers = allocations
    .filter((allocation) => allocation.date !== editedDate && !allocation.isLocked)
    .sort((left, right) => left.date.localeCompare(right.date));
  if (peers.length === 0) throw new Error("Unlock another day before changing this allocation");

  const amount = Math.abs(delta);
  const capacities = delta > 0
    ? peers.map((allocation) => allocation.calories - allocation.minimumCalories)
    : peers.map(() => amount);
  const available = capacities.reduce((sum, capacity) => sum + capacity, 0);
  if (available < amount) {
    const maximum = edited.calories + available;
    throw new Error(`The unlocked days can support at most ${maximum.toLocaleString()} kcal for this day`);
  }

  const transfers = splitEvenlyWithinCapacities(amount, capacities);
  const transferByDate = new Map(peers.map((allocation, index) => [allocation.date, transfers[index]]));
  return allocations.map((allocation) => {
    if (allocation.date === editedDate) return { ...allocation, calories: requestedCalories };
    const transfer = transferByDate.get(allocation.date) ?? 0;
    return {
      ...allocation,
      calories: allocation.calories + (delta > 0 ? -transfer : transfer),
    };
  });
}

function splitEvenlyWithinCapacities(total: number, capacities: readonly number[]): number[] {
  if (total === 0) return capacities.map(() => 0);
  let low = 0;
  let high = Math.max(...capacities);
  let level = 0;

  while (low <= high) {
    const candidate = Math.floor((low + high) / 2);
    const used = capacities.reduce((sum, capacity) => sum + Math.min(capacity, candidate), 0);
    if (used <= total) {
      level = candidate;
      low = candidate + 1;
    } else {
      high = candidate - 1;
    }
  }

  const transfers = capacities.map((capacity) => Math.min(capacity, level));
  let remainder = total - transfers.reduce((sum, transfer) => sum + transfer, 0);
  for (let index = 0; index < transfers.length && remainder > 0; index += 1) {
    if (transfers[index] < capacities[index]) {
      transfers[index] += 1;
      remainder -= 1;
    }
  }
  if (remainder !== 0) throw new Error("Unable to distribute calories within the available floors");
  return transfers;
}

export function replaceCalorieAllocations(
  plan: WeekPlan,
  replacements: Readonly<Record<string, { readonly calories: number; readonly isLocked: boolean }>>,
): WeekPlan {
  const allocations = plan.allocations.map((allocation) => {
    const replacement = replacements[allocation.date];
    return replacement === undefined ? allocation : {
      ...allocation,
      calories: replacement.calories,
      isLocked: replacement.isLocked,
    };
  });
  return validateWeekPlan({ ...plan, allocations });
}

export function resetCalorieAllocations(plan: WeekPlan): WeekPlan {
  const allocations = plan.allocations.map((allocation) => ({
    ...allocation,
    calories: plan.baselineDailyCalories,
    isLocked: false,
  }));
  return validateWeekPlan({ ...plan, allocations });
}

export function moveCaloriesBetweenDays(
  plan: WeekPlan,
  fromDate: CalendarDate,
  toDate: CalendarDate,
  calories: number,
): WeekPlan {
  if (!Number.isInteger(calories) || calories <= 0) throw new Error("Moved calories must be a positive whole number");
  if (fromDate === toDate) throw new Error("Calories must move between different days");
  const floor = calculateDailyCalorieFloor(plan.dailyFloorPolicy, plan.fixedDailyProteinGrams, plan.macroDistribution);
  const fromAllocation = plan.allocations.find((allocation) => allocation.date === fromDate);
  const toAllocation = plan.allocations.find((allocation) => allocation.date === toDate);
  if (!fromAllocation || !toAllocation) throw new Error("Both allocation dates must belong to the week plan");
  if (fromAllocation.isLocked || toAllocation.isLocked) throw new Error("Unlock both days before moving calories");
  let foundFrom = false;
  let foundTo = false;
  const allocations: DailyCalorieAllocation[] = plan.allocations.map((allocation) => {
    if (allocation.date === fromDate) {
      foundFrom = true;
      if (allocation.calories - calories < floor) throw new Error(`Move would breach the daily floor of ${floor}`);
      return { ...allocation, calories: allocation.calories - calories };
    }
    if (allocation.date === toDate) {
      foundTo = true;
      return { ...allocation, calories: allocation.calories + calories };
    }
    return allocation;
  });
  if (!foundFrom || !foundTo) throw new Error("Both allocation dates must belong to the week plan");
  return validateWeekPlan({ ...plan, allocations });
}
